import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import type { User } from '@/services/interfaces/Auth';
import { authService } from '@/services/implementations/AuthService';
import { apiManager } from '@/services/implementations/APIManager';
import type { Auth0User } from '@/services/interfaces/Auth';
import { toast } from 'sonner';

export interface UserProfile {
  user: {
    id: string;
    email: string | null;
    name: string;
    avatar?: string | null;
    mfaEnabled: boolean;
    settings?: string | null;
    created_at: string;
    updated_at: string;
    last_login_at?: string | null;
  };
  subscription: {
    id: string;
    plan_id: string;
    plan_name: string;
    price: number;
    status: string;
    expires_at: string;
    auto_renew: boolean;
  } | null;
  plan: 'premium' | 'free';
  token_usage: {
    unlimited: boolean;
    available_tokens: number; // -1 表示无限制
    used_tokens: number;
  };
}

// 免费试用状态接口
interface FreeTrialInfo {
  isInFreeTrial: boolean;
  remainingTokens: number;
  updateFreeTrialTokens: (tokensUsed: number) => Promise<void>;
  clearFreeTrial: () => void;
}

interface AuthContextType {
  userProfile: UserProfile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string, turnstileToken?: string, code?: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (email: string, password: string, name: string, code: string, turnstileToken?: string, invitationCode?: string) => Promise<User>;
  sendOtp: (email: string, isLogin?: number, purpose?: 'registration' | 'forgot_password' | 'mfa', turnstileToken?: string, invite_code?: string) => Promise<any>;
  verifyOtp: (email: string, code: string, purpose?: 'registration' | 'forgot_password' | 'mfa') => Promise<any>;
  socialLogin: (user: Auth0User) => Promise<void>;
  refreshUserProfile: () => Promise<void>;
  updateUserProfile: (updatedUser: Partial<User>) => Promise<void>;
  updatePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  deleteAccount: () => Promise<void>;
  onLoginSuccess: (callback: () => void) => void;
  // 🔑 修改：token 相关方法，集成免费试用
  hasEnoughTokens: () => boolean;
  getAvailableTokens: () => number;
  updateUserTokens: (tokensUsed: number) => Promise<void>;
  // 🔑 新增：设置免费试用信息的方法
  setFreeTrialInfo: (info: FreeTrialInfo | null) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loginSuccessCallback, setLoginSuccessCallback] = useState<(() => void) | undefined>(undefined);
  const [freeTrialInfo, setFreeTrialInfo] = useState<FreeTrialInfo | null>(null);

  // 🔑 获取Auth0的logout方法
  const { logout: auth0Logout } = useAuth0();

  const isAuthenticated = !!userProfile;

  // 从 localStorage 加载用户配置
  const loadFromLocalStorage = useCallback(() => {
    try {
      const savedProfile = localStorage.getItem('user_data');
      if (savedProfile) {
        setUserProfile(JSON.parse(savedProfile));
      }
    } catch (error) {
      console.error('加载本地用户配置失败:', error);
    }
  }, []);

  // 保存到 localStorage
  const saveToLocalStorage = useCallback((profile: UserProfile | null) => {
    try {
      if (profile) {
        localStorage.setItem('user_data', JSON.stringify(profile));
      } else {
        localStorage.removeItem('user_data');
      }
    } catch (error) {
      console.error('保存用户配置到本地失败:', error);
    }
  }, []);

  // 获取完整的用户配置信息
  const fetchUserProfile = useCallback(async (): Promise<UserProfile | null> => {
    try {
      const profile = await apiManager.getUserProfile();
      setUserProfile(profile);
      saveToLocalStorage(profile);
      return profile;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '获取用户信息失败';
      console.error('获取用户配置失败:', errorMessage);
      return null;
    }
  }, [saveToLocalStorage]);

  // 🔑 检查用户是否有足够的 tokens（集成免费试用）
  const hasEnoughTokens = useCallback(() => {
    // 如果用户已登录，检查用户的token
    if (userProfile) {
      // 如果是无限制账户，返回 true
      if (userProfile.token_usage?.unlimited || userProfile.token_usage?.available_tokens === -1) {
        return true;
      }
      
      // 检查可用 tokens 是否大于 0
      return userProfile.token_usage.available_tokens > 0;
    }
    
    // 如果用户未登录，检查免费试用的token
    if (freeTrialInfo && freeTrialInfo.isInFreeTrial) {
      return freeTrialInfo.remainingTokens > 0;
    }
    
    return false;
  }, [userProfile, freeTrialInfo]);

  // 🔑 获取可用 tokens 数量（用于显示）（集成免费试用）
  const getAvailableTokens = useCallback(() => {
    // 如果用户已登录，返回用户的token
    if (userProfile) {
      if (userProfile.token_usage?.unlimited || userProfile.token_usage?.available_tokens === -1) {
        return -1; // 表示无限制
      }
      
      return userProfile.token_usage?.available_tokens || 0;
    }
    
    // 如果用户未登录，返回免费试用的token
    if (freeTrialInfo && freeTrialInfo.isInFreeTrial) {
      return freeTrialInfo.remainingTokens;
    }
    
    return 0;
  }, [userProfile, freeTrialInfo]);

  // 初始化时检查用户状态
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // 首先从本地存储加载数据
        loadFromLocalStorage();

        // 如果有token，尝试获取最新的用户资料
        if (authService.isAuthenticated()) {
          try {
            await fetchUserProfile();
          } catch (error) {
            console.error('获取用户资料失败:', error);
            // 如果API调用失败，清除认证状态
            localStorage.removeItem('translator_auth_token');
            localStorage.removeItem('user_data');
            setUserProfile(null);
          }
        }
      } catch (error) {
        console.error('初始化认证状态失败:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [loadFromLocalStorage, fetchUserProfile]);

  const login = async (email: string, password: string, turnstileToken?: string, code?: string) => {
    try {
      const response = await authService.loginWithCredentials(email, password, turnstileToken, code);
      
      if (response.requiresMFA) {
        // 需要MFA验证，抛出特殊错误
        throw new Error('MFA_REQUIRED');
      }
      
      if (response.token && response.user) {
        // 登录成功后获取完整的用户配置信息
        await fetchUserProfile();
        
        // 🔑 清除免费试用相关信息
        if (freeTrialInfo) {
          freeTrialInfo.clearFreeTrial();
        }
        
        toast.success('Login successful!');
        
        // Call the success callback if it exists
        if (loginSuccessCallback) {
          loginSuccessCallback();
        }
      }
    } catch (error) {
      throw error;
    }
  };

  const register = async (email: string, password: string, name: string, code: string, turnstileToken?: string, invitationCode?: string): Promise<User> => {
    try {
      const response = await authService.registerWithCredentials(email, password, name, code, turnstileToken, invitationCode);
      
      if (response.token && response.user) {
        // 注册成功后获取完整的用户配置信息
        await fetchUserProfile();
        
        // 🔑 清除免费试用相关信息
        if (freeTrialInfo) {
          freeTrialInfo.clearFreeTrial();
        }
        
        toast.success('Registration successful!');
        
        // Call the success callback if it exists
        if (loginSuccessCallback) {
          loginSuccessCallback();
        }
        
        return response.user;
      }
      
      throw new Error('Registration failed');
    } catch (error) {
      throw error;
    }
  };

  const sendOtp = async (email: string, isLogin?: number, purpose: 'registration' | 'forgot_password' | 'mfa' = 'registration', turnstileToken?: string, invite_code?: string) => {
    try {
      const response = await authService.sendOTPWithParams(email, isLogin, purpose, turnstileToken, invite_code);
      if (response.message) {
        toast.success(response.message);
      }
      return response;
    } catch (error: any) {
      console.error('发送验证码失败:', error);
      toast.error(error.message || 'Failed to send verification code');
      throw error;
    }
  };

  const verifyOtp = async (email: string, code: string, purpose: 'registration' | 'forgot_password' | 'mfa' = 'registration') => {
    try {
      const response = await authService.verifyOTPWithParams(email, code, purpose);
      if (response.message) {
        toast.success(response.message);
      }
      return response;
    } catch (error: any) {
      console.error('验证码验证失败:', error);
      toast.error(error.message || 'Verification code is invalid or expired');
      throw error;
    }
  };

  const socialLogin = async (auth0User: Auth0User) => {
    try {
      console.log('Sending social login data to backend API');
      const response = await authService.socialLogin(auth0User);
      console.log('社交账号登录成功:', response);
      
      if (response.token && response.user) {
        // 社交登录成功后获取完整的用户配置信息
        await fetchUserProfile();
        
        // 🔑 清除免费试用相关信息
        if (freeTrialInfo) {
          freeTrialInfo.clearFreeTrial();
        }
        
        toast.success('Social login successful!');
        
        // Call the success callback if it exists
        if (loginSuccessCallback) {
          loginSuccessCallback();
        }
      }
    } catch (error) {
      console.log('社交账号登录失败:', error);
      console.error('Social login failed:', error);
      toast.error('Social login failed');
      throw error;
    }
  };

  const logout = async () => {
    try {
      // 🔑 首先调用后端logout
      await authService.logout();
      localStorage.removeItem('user_data');
      setUserProfile(null);

      // 🔑 登出时也清除免费试用相关信息（确保清理干净）
      if (freeTrialInfo) {
        freeTrialInfo.clearFreeTrial();
      }

      // 🔑 调用Auth0的logout方法，清除Auth0 session
      // 这会重定向到Auth0配置的logout URL (https://lingoai.tech/login)
      await auth0Logout({
        logoutParams: {
          returnTo: window.location.origin + '/login'
        }
      });

      toast.success('Logout successful!');
    } catch (error) {
      console.error('登出失败:', error);
      toast.error('Logout failed, please try again');
    }
  };

  const refreshUserProfile = async () => {
    try {
      await fetchUserProfile();
    } catch (error) {
      console.error('刷新用户信息失败:', error);
      toast.error('Failed to refresh user information');
      setUserProfile(null);
    }
  };

  // 更新用户资料方法
  const updateUserProfile = async (updatedUser: Partial<User>) => {
    try {
      const response = await authService.updateUserProfile(updatedUser);
      if (response.success) {
        // 更新成功后刷新用户信息
        await fetchUserProfile();
        toast.success('Profile updated successfully!');
      }
    } catch (error: any) {
      console.error('更新用户资料失败:', error);
      toast.error(error.message || 'Failed to update profile');
      throw error;
    }
  };

  // 更新密码方法
  const updatePassword = async (currentPassword: string, newPassword: string) => {
    try {
      const response = await authService.updatePassword({
        currentPassword,
        newPassword
      });
      if (response.success) {
        toast.success('Password updated successfully!');
      }
    } catch (error: any) {
      console.error('更新密码失败:', error);
      toast.error(error.message || 'Failed to update password');
      throw error;
    }
  };

  // 删除账户方法
  const deleteAccount = async () => {
    try {
      const response = await authService.deleteAccount();
      if (response.success) {
        // 删除成功后清除所有本地数据
        localStorage.removeItem('user_data');
        setUserProfile(null);

        // 清除免费试用相关信息
        if (freeTrialInfo) {
          freeTrialInfo.clearFreeTrial();
        }

        toast.success('Account deleted successfully!');
      }
    } catch (error: any) {
      console.error('删除账户失败:', error);
      toast.error(error.message || 'Failed to delete account');
      throw error;
    }
  };

  // 🔑 实时更新token的方法（集成免费试用）
  const updateUserTokens = async (tokensUsed: number) => {
    // 如果用户已登录，更新用户的token
    if (userProfile) {
      setUserProfile(prev => {
        if (!prev) return prev;
        
        // 如果是无限制账户，只更新used_tokens，不扣除available_tokens
        if (prev.token_usage.unlimited || prev.token_usage.available_tokens === -1) {
          const updatedProfile = {
            ...prev,
            token_usage: {
              ...prev.token_usage,
              used_tokens: prev.token_usage.used_tokens + tokensUsed
            }
          };
          saveToLocalStorage(updatedProfile);
          return updatedProfile;
        }
        
        // 对于有限制的账户，扣除available_tokens并增加used_tokens
        const newAvailableTokens = Math.max(0, prev.token_usage.available_tokens - tokensUsed);
        const updatedProfile = {
          ...prev,
          token_usage: {
            ...prev.token_usage,
            available_tokens: newAvailableTokens,
            used_tokens: prev.token_usage.used_tokens + tokensUsed
          }
        };
        saveToLocalStorage(updatedProfile);
        return updatedProfile;
      });
    } else if (freeTrialInfo && freeTrialInfo.isInFreeTrial) {
      // 如果用户未登录，更新免费试用的token
      await freeTrialInfo.updateFreeTrialTokens(tokensUsed);
    }
  };

  const value: AuthContextType = {
    userProfile,
    isLoading,
    isAuthenticated,
    login,
    logout,
    register,
    sendOtp,
    verifyOtp,
    socialLogin,
    refreshUserProfile,
    updateUserProfile,
    updatePassword,
    deleteAccount,
    onLoginSuccess: (callback: () => void) => setLoginSuccessCallback(() => callback),
    hasEnoughTokens,
    getAvailableTokens,
    updateUserTokens,
    setFreeTrialInfo,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 